import * as React from "react";
import "./ai-analysis-ui.scss";
import { CategoryList } from "./CategoryList";
import { IssueDetail } from "./IssueDetail";
import { RunAnalysisButton } from "./RunAnalysisButton";
import { StatusHeader } from "./StatusHeader";
import { useAiAnalysisViewModel } from "./hooks/useAiAnalysisViewModel";
import { useBulkActions } from "./hooks/useBulkActions";
import { usePanelNavigation } from "./hooks/usePanelNavigation";
import { useSuggestionEditor } from "./hooks/useSuggestionEditor";
import type { AiAnalysisPanelProps } from "./types";

export const AiAnalysisPanel: React.FC<AiAnalysisPanelProps> = ({
  aiPhase,
  aiLoading,
  aiStatus,
  aiApplyError,
  aiParagraphs,
  aiRowStatus,
  aiApplyingKey,
  aiRetryingIndex = null,
  aiRetryError = "",
  aiRetryErrorIndex = null,
  onRunAiAnalysis,
  onAcceptSuggestion,
  onRejectSuggestion,
  onRefreshAiAnalysis,
  onRetryParagraph,
  onClearRetryError,
  onRevealIssueInDocument,
}) => {
  const vm = useAiAnalysisViewModel(aiParagraphs, aiRowStatus);

  const editor = useSuggestionEditor();

  const navigation = usePanelNavigation({
    vm,
    isEditing: editor.isEditing,
    onDiscardEditConfirm: editor.confirmDiscardEdit,
    onRevealIssueInDocument,
  });

  const bulk = useBulkActions({
    pendingNonCompliantKeys: vm.pendingNonCompliantKeys,
    editedTextByKey: editor.editedTextByKey,
    aiLoading,
    aiApplyingKey,
    clearEditState: editor.clearEditState,
    onAcceptSuggestion,
    onRejectSuggestion,
    onRefreshAiAnalysis,
  });

  const [pulseHighlight, setPulseHighlight] = React.useState(false);
  const prevRetryingRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (!navigation.selectedIssue) return;
    const idx = navigation.selectedIssue.row.index;
    const stillExists = vm.rows.some((p) => p.index === idx);
    if (!stillExists) {
      navigation.closeDetail();
      return;
    }
    navigation.syncSelectedIssue();
  }, [vm.rows]);

  React.useEffect(() => {
    const wasRetrying = prevRetryingRef.current;
    prevRetryingRef.current = aiRetryingIndex ?? null;
    if (wasRetrying !== null && aiRetryingIndex === null && navigation.selectedIssue) {
      setPulseHighlight(true);
      const t = setTimeout(() => setPulseHighlight(false), 200);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [aiRetryingIndex, navigation.selectedIssue]);

  const hasResults = vm.rows.length > 0;
  const showResults = aiParagraphs !== null && hasResults;
  const showStatusHeader = aiLoading || aiParagraphs !== null;
  const runFailed = Boolean(aiApplyError) && !aiLoading && aiPhase === "run" && !hasResults;
  const showEmptyRunCta = !hasResults && !runFailed && !aiLoading;

  const selected = navigation.selectedIssue;
  const showLoadingList = aiLoading && !selected && !hasResults;
  const selectedStatus = selected
    ? (aiRowStatus[selected.key] ?? selected.row.reviewStatus ?? "pending")
    : "pending";
  const selectedBusy =
    selected !== null &&
    (aiApplyingKey === selected.key || bulk.bulkBusy);
  const selectedRetrying = selected !== null && aiRetryingIndex === selected.row.index;
  const selectedRetryError =
    selected !== null &&
    aiRetryErrorIndex === selected.row.index &&
    aiRetryError
      ? aiRetryError
      : undefined;

  const handleApplyEdit = async () => {
    if (!selected) return;
    await Promise.resolve(
      onAcceptSuggestion(selected.row, selected.key, {
        overrideText: editor.draftText,
      })
    );
    editor.clearEditState(selected.key);
  };

  const handleAccept = async () => {
    if (!selected) return;
    const overrideText = editor.editedTextByKey[selected.key];
    await Promise.resolve(
      onAcceptSuggestion(selected.row, selected.key, {
        ...(overrideText !== undefined ? { overrideText } : {}),
      })
    );
    editor.clearEditState(selected.key);
  };

  const handleReject = async () => {
    if (!selected) return;
    editor.clearEditState(selected.key);
    await Promise.resolve(onRejectSuggestion(selected.row, selected.key));
  };

  const handleRetry = () => {
    if (!selected || !onRetryParagraph) return;
    if (editor.hasUserEdit(selected.key)) {
      if (!window.confirm("Discard your edit and regenerate?")) return;
      editor.clearEditState(selected.key);
    }
    onClearRetryError?.();
    void onRetryParagraph(selected.row, selected.key);
  };

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (!selected) return;
    e.stopPropagation();
    if (e.key === "Escape") {
      e.preventDefault();
      if (window.confirm("Discard edit?")) {
        editor.cancelEdit(selected.key);
      }
    } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void handleApplyEdit();
    }
  };

  return (
    <div className="ai-analysis">
      {showStatusHeader ? (
        <StatusHeader
          reviewsNeededLabel={vm.reviewsNeededLabel}
          needsAttentionCount={vm.needsAttentionCount}
          needsReviewCount={vm.needsReviewCount}
          compliantCount={vm.compliantCount}
          totalRows={vm.rows.length}
          hasResults={hasResults}
          loading={aiLoading}
          running={aiLoading && aiPhase === "run"}
          showError={runFailed}
          errorMessage={aiApplyError}
          onRetryRun={onRunAiAnalysis}
          onRerun={hasResults || (aiLoading && aiPhase === "run") ? onRunAiAnalysis : undefined}
          pendingCount={vm.pendingNonCompliantKeys.length}
          confirmAction={bulk.confirmAction}
          bulkBusy={bulk.bulkBusy}
          acceptAllRunning={bulk.acceptAllRunning}
          rejectAllRunning={bulk.rejectAllRunning}
          onAcceptAllClick={bulk.onAcceptAllClick}
          onRejectAllClick={bulk.onRejectAllClick}
          onConfirmAcceptAll={() => void bulk.handleAcceptAll()}
          onConfirmRejectAll={() => void bulk.handleRejectAll()}
          onCancelConfirm={bulk.cancelConfirm}
        />
      ) : (
        <div className="ai-analysis__status-header">
          <div className="ai-analysis__status-row">
            <p className="ai-analysis__status-title">AI analysis</p>
          </div>
        </div>
      )}

      {aiLoading && aiStatus && !selected ? (
        <p className="ai-analysis__meta" aria-live="polite" style={{ margin: 0 }}>
          {aiStatus}
        </p>
      ) : null}

      {showEmptyRunCta ? (
        <RunAnalysisButton
          hasResults={hasResults}
          loading={aiLoading}
          onClick={onRunAiAnalysis}
        />
      ) : null}

      {aiApplyError && !runFailed ? (
        <div className="ai-analysis__apply-error" role="alert">
          {aiApplyError}
        </div>
      ) : null}

      {selected ? (
        <IssueDetail
          selected={selected}
          activeTab={navigation.activeTab}
          status={selectedStatus}
          isRetrying={selectedRetrying}
          retryError={selectedRetryError}
          busy={selectedBusy}
          isEditing={editor.editingKey === selected.key}
          hasAppliedEdit={editor.hasAppliedEdit(selected.key)}
          draftText={editor.draftText}
          displayOverride={editor.getDisplayText(selected.key, selected.row)}
          tabsDisabled={editor.editingKey === selected.key}
          onClose={navigation.closeDetail}
          onTabChange={navigation.requestTabChange}
          onDraftChange={editor.setDraftText}
          onEnterEdit={() => editor.enterEditMode(selected.key, selected.row)}
          onApplyEdit={() => void handleApplyEdit()}
          onCancelEdit={() => editor.cancelEdit(selected.key)}
          onRetry={handleRetry}
          onClearRetryError={onClearRetryError}
          onAccept={() => void handleAccept()}
          onReject={() => void handleReject()}
          onEditKeyDown={handleEditKeyDown}
          onDetailKeyDown={navigation.handleDetailKeyDown}
          pulseHighlight={pulseHighlight}
        />
      ) : showResults || showLoadingList ? (
        <CategoryList
          vm={vm}
          categoryExpansion={navigation.categoryExpansion}
          selectedKey={null}
          focusedKey={navigation.focusedRowKey}
          aiRowStatus={aiRowStatus}
          onToggleCategory={navigation.toggleCategory}
          onExpandCategory={navigation.expandCategory}
          onCollapseCategory={navigation.collapseCategory}
          onOpenIssue={navigation.openIssue}
          onListKeyDown={navigation.handleListKeyDown}
          loading={aiLoading}
        />
      ) : null}
    </div>
  );
};
